import {useCallback, useEffect, useRef, useState} from 'react'
import {
  type MatrixEvent,
  MsgType,
  NotificationCountType,
  type Room,
  type RoomMember,
} from 'matrix-js-sdk'
import {
  type AudioInfo,
  type EncryptedFile,
  type FileInfo,
  type ImageInfo,
  type VideoInfo,
} from 'matrix-js-sdk/lib/@types/media'

import {LOCAL_ROOM_PREFIX} from '#/lib/constants'
import {logger} from '#/logger'
import {useMatrixContext} from '#/state/matrix'
import {useDirectRooms} from './useDirectRooms'

export type MatrixReaction = {
  eventId: string // the m.reaction event's own ID (needed for redaction)
  senderUserId: string
  senderName: string
  emoji: string
}

export type MatrixMessageAttachment = {
  msgtype: 'm.image' | 'm.video' | 'm.file' | 'm.audio'
  url?: string // mxc:// URI (unencrypted)
  file?: EncryptedFile // encrypted file info (future E2EE support)
  info?: ImageInfo | VideoInfo | AudioInfo | FileInfo
  filename?: string
}

export type MatrixMessage = {
  eventId: string
  sender: string
  senderName: string
  senderAvatar?: string
  body: string
  timestamp: number
  type: string
  isMe: boolean
  reactions: MatrixReaction[]
  attachment?: MatrixMessageAttachment
  sendStatus?: 'sending' | 'sent' | 'failed'
  localUri?: string // local file URI for optimistic display before upload completes
}

export type MatrixRoomMember = {
  userId: string
  did: string | null
  displayName: string
  avatarUrl?: string
}

/** Convert Matrix localpart back to DID: did_plc_abc123 -> did:plc:abc123 */
function localpartToDid(localpart: string): string | null {
  const match = localpart.match(/^(did)_([a-z]+)_(.+)$/)
  if (!match) return null
  return `${match[1]}:${match[2]}:${match[3]}`
}

const ATTACHMENT_MSGTYPES = new Set([
  MsgType.Image,
  MsgType.Video,
  MsgType.File,
  MsgType.Audio,
])

function eventToMessage(
  event: MatrixEvent,
  myUserId: string,
  room: Room,
): MatrixMessage | null {
  if (event.getType() !== 'm.room.message') return null
  const content = event.getContent()
  if (!content.body) return null

  const sender = event.getSender()
  const member = sender ? room.getMember(sender) : null
  const msgtype = (content.msgtype as string) || 'm.text'

  let attachment: MatrixMessageAttachment | undefined
  if (ATTACHMENT_MSGTYPES.has(msgtype as MsgType)) {
    attachment = {
      msgtype: msgtype as MatrixMessageAttachment['msgtype'],
      url: content.url as string | undefined,
      file: content.file as EncryptedFile | undefined,
      info: content.info as
        | ImageInfo
        | VideoInfo
        | AudioInfo
        | FileInfo
        | undefined,
      filename: (content.filename as string) || (content.body as string),
    }
  }

  return {
    eventId: event.getId() || '',
    sender: sender || '',
    senderName: member?.name || sender || 'Unknown',
    senderAvatar:
      member?.getAvatarUrl(
        room.client.getHomeserverUrl(),
        40,
        40,
        'crop',
        false,
        false,
      ) || undefined,
    body: content.body as string,
    timestamp: event.getTs(),
    type: msgtype,
    isMe: sender === myUserId,
    reactions: [],
    attachment,
  }
}

export function useMatrixRoom(roomId: string) {
  const {client, isConnected} = useMatrixContext()
  const {setDirectRoom} = useDirectRooms()
  const [messages, setMessages] = useState<MatrixMessage[]>([])
  const [members, setMembers] = useState<MatrixRoomMember[]>([])
  const [roomName, setRoomName] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [room, setRoom] = useState<Room | null>(
    () => client?.getRoom(roomId) || null,
  )
  const myUserId = client?.getUserId() || ''

  // Local room support
  const isLocalRoom = roomId.startsWith(LOCAL_ROOM_PREFIX)
  const targetMatrixUserId = isLocalRoom
    ? roomId.slice(LOCAL_ROOM_PREFIX.length)
    : null
  const [realRoomId, setRealRoomId] = useState<string | null>(null)
  const isPromoting = useRef(false)

  // For local rooms, don't wait for SDK — show immediately
  useEffect(() => {
    if (isLocalRoom) {
      setIsLoading(false)
      // Set a placeholder member from the target user ID
      if (targetMatrixUserId) {
        const localpartMatch = targetMatrixUserId.match(/^@([^:]+):/)
        const did = localpartMatch ? localpartToDid(localpartMatch[1]) : null
        setMembers([
          {
            userId: targetMatrixUserId,
            did,
            displayName: '',
            avatarUrl: undefined,
          },
        ])
      }
    }
  }, [isLocalRoom, targetMatrixUserId])

  // When the room isn't in the SDK cache yet, listen for it to appear
  useEffect(() => {
    if (isLocalRoom) return // Local rooms don't exist in SDK
    const existing = client?.getRoom(roomId) || null
    if (existing) {
      setRoom(existing)
      return
    }
    if (!client || !isConnected) return

    const onRoom = (newRoom: Room) => {
      if (newRoom.roomId === roomId) {
        setRoom(newRoom)
      }
    }
    client.on('Room' as any, onRoom)
    return () => {
      client.removeListener('Room' as any, onRoom)
    }
  }, [client, isConnected, roomId, isLocalRoom])

  const refreshMessages = useCallback(() => {
    if (!room || !client) return

    const timeline = room.getLiveTimeline().getEvents()

    // First pass: collect reactions keyed by target message event ID
    const reactionsMap = new Map<string, MatrixReaction[]>()
    for (const event of timeline) {
      if (event.getType() === 'm.reaction' && !event.isRedacted()) {
        const content = event.getContent()
        const relatesTo = content['m.relates_to']
        if (
          relatesTo?.rel_type === 'm.annotation' &&
          relatesTo.event_id &&
          relatesTo.key
        ) {
          const targetId = relatesTo.event_id as string
          const sender = event.getSender()
          const member = sender ? room.getMember(sender) : null
          const reaction: MatrixReaction = {
            eventId: event.getId() || '',
            senderUserId: sender || '',
            senderName: member?.name || sender || 'Unknown',
            emoji: relatesTo.key as string,
          }
          const existing = reactionsMap.get(targetId) || []
          // Deduplicate by (senderUserId, emoji)
          if (
            !existing.some(
              r =>
                r.senderUserId === reaction.senderUserId &&
                r.emoji === reaction.emoji,
            )
          ) {
            existing.push(reaction)
            reactionsMap.set(targetId, existing)
          }
        }
      }
    }

    // Second pass: build messages with attached reactions
    const msgs = timeline
      .map(event => eventToMessage(event, myUserId, room))
      .filter((m): m is MatrixMessage => m !== null)
      .map(msg => ({
        ...msg,
        reactions: reactionsMap.get(msg.eventId) || [],
      }))

    setMessages(msgs)
    setRoomName(room.name || 'Unnamed Chat')
    // Include joined + invited members (invited members haven't accepted yet)
    const allMembers = room.currentState.getMembers().filter(m => {
      const membership = m.membership
      return membership === 'join' || membership === 'invite'
    })
    setMembers(
      allMembers.map((m: RoomMember) => {
        const localpartMatch = m.userId.match(/^@([^:]+):/)
        const did = localpartMatch ? localpartToDid(localpartMatch[1]) : null
        return {
          userId: m.userId,
          did,
          displayName: m.name || m.userId,
          avatarUrl:
            m.getAvatarUrl(
              client.getHomeserverUrl(),
              40,
              40,
              'crop',
              false,
              false,
            ) || undefined,
        }
      }),
    )
    setIsLoading(false)
  }, [room, client, myUserId])

  const markAsRead = useCallback(async () => {
    if (!client || !room) return

    // Optimistic: clear counts locally for instant badge removal
    room.setUnreadNotificationCount(NotificationCountType.Highlight, 0)
    room.setUnreadNotificationCount(NotificationCountType.Total, 0)

    // Send read receipt to server
    const lastEvent = room.getLastLiveEvent()
    if (!lastEvent) return
    try {
      await client.sendReadReceipt(lastEvent)
    } catch (err) {
      logger.warn('Failed to send read receipt', {safeMessage: err})
    }
  }, [client, room])

  useEffect(() => {
    if (!client || !isConnected || !room) return

    refreshMessages()

    const onTimeline = () => {
      refreshMessages()
      markAsRead()
    }
    client.on('Room.timeline' as any, onTimeline)

    return () => {
      client.removeListener('Room.timeline' as any, onTimeline)
    }
  }, [client, isConnected, room, refreshMessages, markAsRead])

  const sendMessage = useCallback(
    async (body: string) => {
      if (!client) return
      const targetRoomId = realRoomId || roomId
      await client.sendTextMessage(targetRoomId, body)
    },
    [client, roomId, realRoomId],
  )

  const sendAttachment = useCallback(
    async (attachment: {
      msgtype: 'm.image' | 'm.video' | 'm.file' | 'm.audio'
      url: string // mxc:// URI from uploadContent
      body: string // filename
      info: ImageInfo | VideoInfo | AudioInfo | FileInfo
      filename?: string
    }) => {
      if (!client) return
      const targetRoomId = realRoomId || roomId
      await client.sendMessage(targetRoomId, {
        msgtype: attachment.msgtype as any,
        url: attachment.url,
        body: attachment.body,
        info: attachment.info,
        filename: attachment.filename || attachment.body,
      } as any)
    },
    [client, roomId, realRoomId],
  )

  const loadEarlierMessages = useCallback(async () => {
    if (!client || !room) return
    await client.scrollback(room, 30)
    refreshMessages()
  }, [client, room, refreshMessages])

  const sendReaction = useCallback(
    async (targetEventId: string, emoji: string) => {
      if (!client) return
      const targetRoomId = realRoomId || roomId
      await client.sendEvent(targetRoomId, 'm.reaction' as any, {
        'm.relates_to': {
          rel_type: 'm.annotation',
          event_id: targetEventId,
          key: emoji,
        },
      })
    },
    [client, roomId, realRoomId],
  )

  const removeReaction = useCallback(
    async (reactionEventId: string) => {
      if (!client) return
      const targetRoomId = realRoomId || roomId
      await client.redactEvent(targetRoomId, reactionEventId)
    },
    [client, roomId, realRoomId],
  )

  /**
   * Promote a local room to a real Matrix room.
   * Creates the room on the server, sends the first message, and returns the real room ID.
   */
  const promoteLocalRoom = useCallback(
    async (firstMessage: string): Promise<string | null> => {
      if (!client || !targetMatrixUserId || isPromoting.current) return null
      isPromoting.current = true

      try {
        const result = await client.createRoom({
          invite: [targetMatrixUserId],
          is_direct: true,
          preset: 'private_chat' as any,
        })

        const newRoomId = result.room_id

        // Record in m.direct (fire-and-forget)
        setDirectRoom(targetMatrixUserId, newRoomId).catch(err => {
          logger.warn('Failed to update m.direct', {safeMessage: err})
        })

        // Send the first message
        await client.sendTextMessage(newRoomId, firstMessage)

        // Update internal state so subsequent messages use the real room
        setRealRoomId(newRoomId)

        return newRoomId
      } catch (error) {
        logger.error('Failed to promote local room', {safeMessage: error})
        throw error
      } finally {
        isPromoting.current = false
      }
    },
    [client, targetMatrixUserId, setDirectRoom],
  )

  return {
    messages,
    members,
    myUserId,
    roomName,
    isLoading,
    isLocalRoom,
    realRoomId,
    sendMessage,
    sendAttachment,
    sendReaction,
    removeReaction,
    markAsRead,
    loadEarlierMessages,
    promoteLocalRoom,
    room,
  }
}
